import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Button, EmptyState, Skeleton } from '@nokowazzi/ui';
import { useAuthStore } from '../stores/auth';
import { useRoomsQuery } from '../features/chat/api';
import type { ChatRoomListItemDto } from '../features/chat/api';
import { useUserChannel } from '../features/chat/realtime';
import { chatPreviewText } from '../features/chat/preview';
import { formatRelativeTime } from '../features/sourcing/format';
import { avatarUrl } from '../features/sourcing/urls';

export const Route = createFileRoute('/_tabs/chat')({
  component: ChatListScreen,
});

function ChatListScreen() {
  const { t } = useTranslation();
  const session = useAuthStore((state) => state.session);
  const initializing = useAuthStore((state) => state.initializing);

  useUserChannel();

  return (
    <div className="flex flex-col">
      <header className="px-5 pt-[calc(var(--safe-top)+16px)] pb-2">
        <h1 className="text-title1 text-fg">{t('chat.title')}</h1>
      </header>
      {initializing ? (
        <RoomListSkeleton />
      ) : session === null ? (
        <SignedOutView />
      ) : (
        <RoomList />
      )}
    </div>
  );
}

function SignedOutView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <EmptyState
      className="mt-16"
      icon={<MessageCircle size={48} strokeWidth={1.5} />}
      title={t('errors.unauthorized')}
      description={t('my.signInPromptDescription')}
      action={
        <Button
          onClick={() => void navigate({ to: '/auth/signin', search: { redirect: '/chat' } })}
        >
          {t('actions.signIn')}
        </Button>
      }
    />
  );
}

function RoomListSkeleton() {
  return (
    <div className="flex flex-col gap-1 px-5 pt-2">
      {[0, 1, 2].map((index) => (
        <div key={index} className="flex items-center gap-3 py-3">
          <Skeleton variant="circle" className="size-14 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton variant="text" className="w-32" />
            <Skeleton variant="text" className="w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function RoomList() {
  const { t } = useTranslation();
  const roomsQuery = useRoomsQuery();

  if (roomsQuery.isPending) {
    return <RoomListSkeleton />;
  }

  if (roomsQuery.isError || roomsQuery.data === undefined) {
    return (
      <EmptyState
        className="mt-8"
        icon={<MessageCircle size={48} strokeWidth={1.5} />}
        title={t('errors.generic')}
        action={
          <Button variant="tertiary" onClick={() => void roomsQuery.refetch()}>
            {t('actions.retry')}
          </Button>
        }
      />
    );
  }

  if (roomsQuery.data.length === 0) {
    return (
      <EmptyState
        className="mt-16"
        icon={<MessageCircle size={48} strokeWidth={1.5} />}
        title={t('chat.empty')}
      />
    );
  }

  return (
    <ul className="flex flex-col pb-4">
      {roomsQuery.data.map((item) => (
        <li key={item.room.id}>
          <RoomRow item={item} />
        </li>
      ))}
    </ul>
  );
}

function RoomRow({ item }: { item: ChatRoomListItemDto }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { room, counterpart, request, unread } = item;
  const viewerLocale = i18n.language.startsWith('ja') ? 'ja' : 'ko';
  const preview = chatPreviewText(item.lastMessage, viewerLocale, t);

  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/chat/$roomId', params: { roomId: room.id } })}
      className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors duration-100 select-none hover:bg-subtle active:bg-neutral-100 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gold-600"
    >
      <Avatar
        name={counterpart.display_name}
        src={avatarUrl(counterpart.avatar_path)}
        size="lg"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 truncate text-body font-semibold text-fg">
            {counterpart.display_name}
          </span>
          {room.last_message_at !== null ? (
            <span className="ml-auto shrink-0 text-caption2 whitespace-nowrap text-fg-tertiary">
              {formatRelativeTime(room.last_message_at, t)}
            </span>
          ) : null}
        </span>
        <span className="truncate text-caption2 text-fg-tertiary">{request.title}</span>
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-caption1 text-fg-secondary">
            {preview !== '' ? preview : ' '}
          </span>
          {unread > 0 ? (
            <Badge tone="danger" className="shrink-0 tabular-nums">
              {unread}
            </Badge>
          ) : null}
        </span>
      </span>
    </button>
  );
}
